import React from 'react';
import { Award, Users, Target, Globe } from 'lucide-react';
import SEO from '../components/SEO';

export default function About() {
  const showCertifications = false;
  const canonical = 'https://divaisetech.com/about';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Divaise Technology',
    url: canonical,
    mainEntity: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      description:
        'Divaise Technology provides AI-based IT solutions, automation, digital engineering, and hardware enablement services.',
    },
  };

  const team = [
    {
      role: 'Founder & CEO',
      description: 'Leads strategic direction across AI solutions, architecture, and enterprise delivery.',
      expertise: 'Cloud Strategy, Enterprise Architecture',
    },
    {
      role: 'VP Engineering',
      description: 'Oversees platform engineering, automation standards, and delivery quality.',
      expertise: 'DevOps, Infrastructure, Team Building',
    },
    {
      role: 'VP Solutions',
      description: 'Shapes client solutions with a focus on business alignment and practical execution.',
      expertise: 'Consulting, Client Relations, Business Strategy',
    },
    {
      role: 'Training & QA Lead',
      description: 'Drives quality assurance, process consistency, and knowledge enablement.',
      expertise: 'QA, Security, Compliance, Training',
    },
  ];

  const certifications = [
    { name: 'ISO 27001:2013', description: 'Information Security Management' },
    { name: 'SOC 2 Type II', description: 'Security, Availability, Processing Integrity' },
    { name: 'AWS Premier Partner', description: 'Advanced Tier Partner' },
    { name: 'Microsoft Gold Partner', description: 'Cloud Solutions Competency' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We deliver superior quality in every project through attention to detail and continuous improvement.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We work closely with our clients as true partners, understanding their unique needs and challenges.',
    },
    {
      icon: Globe,
      title: 'Innovation',
      description: 'We stay ahead of technology trends to provide cutting-edge solutions for our clients.',
    },
    {
      icon: Award,
      title: 'Integrity',
      description: 'We maintain transparency, honesty, and accountability in all our interactions and deliverables.',
    },
  ];

  return (
    <div>
      <SEO
        title="About Divaise Technology - IT Consultancy Excellence"
        description="Learn about Divaise Technology and our approach to AI-based IT solutions, automation, hardware fulfillment, and AI chip-focused delivery."
        keywords="about Divaise, AI solutions, IT automation, hardware fulfillment, AI chips"
        canonical={canonical}
        schema={aboutSchema}
      />

      {/* Hero Section */}
      <section className="hero-with-image hero-about text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">About Divaise Technology</h1>
          <p className="text-xl opacity-90 max-w-3xl">
            We focus on AI-based IT solutions and automations,
            hardware requirement fulfillment, and AI-based chips.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-white">Our Story</h2>
              <p className="text-white/80 mb-4 leading-relaxed">
                Divaise Technology helps organizations adopt practical AI with clarity and measurable outcomes.
                Our core focus is AI-based IT solutions and business automations that support real operations.
              </p>
              <p className="text-white/80 mb-4 leading-relaxed">
                Alongside software-led transformation, we also support hardware requirement fulfillment where
                AI-based chips are our niche. This allows clients to align infrastructure and intelligence together.
              </p>
              <p className="text-white/80 leading-relaxed">
                We continue to refine our AI-first delivery model, staying committed to quality, transparency,
                and long-term client success.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-8 text-white">
              <div className="space-y-8">
                <div>
                  <p className="text-4xl font-bold">15+</p>
                  <p className="text-lg opacity-90">Delivery Domains</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">50+</p>
                  <p className="text-lg opacity-90">Successful Projects</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">100%</p>
                  <p className="text-lg opacity-90">Client Satisfaction Rate</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">$50M+</p>
                  <p className="text-lg opacity-90">Value Delivered</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-12 mb-16">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">Our Mission</h3>
                <p className="text-white/80 leading-relaxed">
                  To empower businesses of all sizes to scale efficiently, innovate rapidly, and stay secure in an
                  ever-changing digital landscape through world-class IT consulting and technology solutions.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">Our Vision</h3>
                <p className="text-white/80 leading-relaxed">
                  To be the most trusted technology partner for enterprises seeking to transform their business
                  operations and achieve sustainable growth through innovation and operational excellence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Our Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={value.title}
                  className="bg-white border border-border rounded-xl p-8 text-center shadow-sm hover:shadow-lg transition-all duration-300 hover:border-accent"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 bg-accent/20 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-accent" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-black">{value.title}</h3>
                  <p className="text-gray-700">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="bg-gray-900 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Leadership Team</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, idx) => (
              <div key={idx} className="bg-gray-800 border border-gray-700 rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-xl">
                  {member.role.charAt(0)}
                </div>
                <h3 className="text-lg font-bold text-center mb-2 text-white">{member.role}</h3>
                <p className="text-sm text-white/80 mb-4 text-center">{member.description}</p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-xs font-semibold text-accent uppercase tracking-wider">Expertise</p>
                  <p className="text-sm font-medium text-white/90">{member.expertise}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showCertifications && (
        <section className="bg-black py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-16 text-white">Certifications & Partnerships</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {certifications.map((cert, idx) => (
                <div
                  key={idx}
                  className="bg-gray-900 border border-gray-700 rounded-xl p-8 text-center"
                >
                  <div className="text-4xl mb-4">🏆</div>
                  <h3 className="text-lg font-bold mb-2 text-white">{cert.name}</h3>
                  <p className="text-white/80 text-sm">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-primary text-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Partner With Us?</h2>
          <p className="text-lg opacity-90 mb-8">
            Let's discuss how we can help transform your business through technology.
          </p>
          <a
            href="/contact"
            className="inline-block bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all hover:shadow-lg"
          >
            Schedule a Consultation
          </a>
        </div>
      </section>
    </div>
  );
}
